// API Health Monitor - Detects when the connect_api backend is unavailable
// and redirects users to an error page after consecutive failures

import * as Sentry from '@sentry/react';

interface FailureRecord {
  timestamp: number;
  status: number | 'network';
  url: string;
  method: string;
}

interface ApiHealthState {
  failures: FailureRecord[];
  redirected: boolean;
}

// Configuration
const CONFIG = {
  // Number of failures required to trigger redirect
  FAILURE_THRESHOLD: 3,
  // Time window - failures older than this don't count (10 seconds)
  TIME_WINDOW_MS: 10_000,
  // Debounce rapid failures to the SAME endpoint only (1 second)
  // Different endpoints failing rapidly still count as separate failures
  SAME_ENDPOINT_DEBOUNCE_MS: 1_000,
  // Status codes considered server errors (API is down)
  SERVER_ERROR_CODES: [500, 501, 502, 503, 504, 520, 521, 522, 523, 524],
  // Network-only trips get one reachability check before redirecting
  NETWORK_RETRY_DELAY_MS: 1_500,
  NETWORK_RETRY_TIMEOUT_MS: 5_000,
  NETWORK_RETRY_PATH: '/api/auth/csrf_token',
};

// Endpoints that might fail normally and should not count toward health monitoring
const EXCLUDED_ENDPOINTS = [
  '/api/auth/csrf_token',
  '/api/users/masquerading_status',
  '/api/users/intercom_settings',
];

// Public routes where monitoring should be disabled
const PUBLIC_ROUTES = [
  '/register',
  '/registration_confirmation',
  '/unauthorized',
  '/prohibited',
  '/terms',
  '/auth/callback',
  '/logout',
  '/users/verify_email_change',
  '/users/verify_recovery_email',
];

class ApiHealthMonitor {
  private state: ApiHealthState = {
    failures: [],
    redirected: false,
  };

  // Shared by every caller that trips at once, so only one reachability check runs
  private pendingTrip: Promise<boolean> | null = null;

  /**
   * Check if a status code indicates a server error (API is down)
   */
  isServerError(status: number): boolean {
    return CONFIG.SERVER_ERROR_CODES.includes(status);
  }

  /**
   * Check if an endpoint should be excluded from monitoring
   */
  isExcludedEndpoint(url: string): boolean {
    return EXCLUDED_ENDPOINTS.some(endpoint => url.includes(endpoint));
  }

  /**
   * Check if current page should be monitored
   * Public routes (auth callback, registration, etc.) are excluded
   */
  shouldMonitor(): boolean {
    const pathname = window.location.pathname;
    return !PUBLIC_ROUTES.some(route => pathname.startsWith(route));
  }

  /**
   * Remove failures older than the time window
   */
  private pruneStaleFailures(): void {
    const cutoff = Date.now() - CONFIG.TIME_WINDOW_MS;
    this.state.failures = this.state.failures.filter(f => f.timestamp > cutoff);
  }

  /**
   * Check if this is a rapid duplicate failure to the same endpoint
   * Only debounces if the SAME URL failed within SAME_ENDPOINT_DEBOUNCE_MS
   * Different URLs failing rapidly should count as separate failures
   */
  private isDuplicateFailure(url: string): boolean {
    const now = Date.now();
    const recentSameUrlFailure = this.state.failures.find(
      f => f.url === url && (now - f.timestamp) < CONFIG.SAME_ENDPOINT_DEBOUNCE_MS
    );
    return !!recentSameUrlFailure;
  }

  /**
   * Record a server error or network failure
   */
  recordFailure(status: number | 'network', url: string, method: string): void {
    // Skip if already redirected
    if (this.state.redirected) {
      return;
    }

    // Skip excluded endpoints
    if (this.isExcludedEndpoint(url)) {
      console.debug('[ApiHealthMonitor] Skipping excluded endpoint:', url);
      return;
    }

    // Skip if same endpoint failed very recently (retry loop protection)
    if (this.isDuplicateFailure(url)) {
      console.debug('[ApiHealthMonitor] Skipping duplicate failure for same URL:', url);
      return;
    }

    // Prune stale failures first
    this.pruneStaleFailures();

    // Record this failure
    const failure: FailureRecord = {
      timestamp: Date.now(),
      status,
      url,
      method,
    };
    this.state.failures.push(failure);

    console.warn('[ApiHealthMonitor] Recorded failure:', {
      failure,
      totalFailures: this.state.failures.length,
      threshold: CONFIG.FAILURE_THRESHOLD,
    });
  }

  /**
   * Record a successful request - resets the failure counter
   * Any 2xx or 4xx response indicates the API is reachable
   */
  recordSuccess(): void {
    if (this.state.redirected) {
      return;
    }

    // Reset failures on any successful request
    if (this.state.failures.length > 0) {
      console.debug('[ApiHealthMonitor] Clearing failures on success, count was:', this.state.failures.length);
      this.state.failures = [];
    }
  }

  /**
   * Check if we should redirect to the error page
   */
  shouldRedirect(): boolean {
    // Prune stale failures before checking
    this.pruneStaleFailures();
    return this.state.failures.length >= CONFIG.FAILURE_THRESHOLD;
  }

  /**
   * Decide a tripped monitor: resolves true when the user is being sent to the error page.
   * Network-only failures can be a momentary blip (Wi-Fi drop, laptop waking), so they get one
   * reachability check first; a sustained outage or CDN/WAF block fails that check too.
   */
  handleTrip(): Promise<boolean> {
    if (this.state.redirected) return Promise.resolve(true);
    if (!this.pendingTrip) {
      this.pendingTrip = this.decideTrip().finally(() => {
        this.pendingTrip = null;
      });
    }
    return this.pendingTrip;
  }

  private async decideTrip(): Promise<boolean> {
    const networkOnly = this.state.failures.every(f => f.status === 'network');
    if (networkOnly) {
      const probeBase = this.state.failures[0]?.url;
      await new Promise(resolve => setTimeout(resolve, CONFIG.NETWORK_RETRY_DELAY_MS));
      // Another request succeeding during the wait already proves the API is reachable.
      if (!this.shouldRedirect()) return false;
      if (await this.apiReachable(probeBase)) {
        this.reportRecovered();
        this.state.failures = [];
        return false;
      }
    }
    this.redirectToErrorPage();
    return true;
  }

  private async apiReachable(failedUrl: string | undefined): Promise<boolean> {
    try {
      const origin = new URL(failedUrl ?? '', window.location.origin).origin;
      const response = await fetch(`${origin}${CONFIG.NETWORK_RETRY_PATH}`, {
        credentials: 'include',
        cache: 'no-store',
        signal: AbortSignal.timeout(CONFIG.NETWORK_RETRY_TIMEOUT_MS),
      });
      return response.ok || (response.status >= 400 && response.status < 500);
    } catch {
      return false;
    }
  }

  private reportRecovered(): void {
    Sentry.captureMessage('API health monitor recovered on retry', {
      level: 'warning',
      tags: { health_monitor: 'recovered', failure_kind: 'network' },
      extra: { failures: this.state.failures, pathname: window.location.pathname },
    });
  }

  /**
   * Redirect to the error page
   */
  redirectToErrorPage(): void {
    // Prevent multiple redirects
    if (this.state.redirected) {
      return;
    }
    this.state.redirected = true;

    const errorPageUrl = import.meta.env.ERROR_PAGE_URL;

    if (!errorPageUrl) {
      this.reportToSentry();
      console.error('[ApiHealthMonitor] ERROR_PAGE_URL not configured - cannot redirect');
      return;
    }

    // Build URL with debug params for troubleshooting
    const url = new URL(errorPageUrl);
    url.searchParams.set('source', 'api_health_monitor');
    url.searchParams.set('app', 'portal');
    url.searchParams.set('failures', String(this.state.failures.length));

    // Log detailed info for debugging
    console.error('[ApiHealthMonitor] Redirecting to error page', {
      reason: 'consecutive_api_failures',
      failures: this.state.failures,
      threshold: CONFIG.FAILURE_THRESHOLD,
      timeWindowMs: CONFIG.TIME_WINDOW_MS,
      redirectUrl: url.toString(),
    });

    this.reportToSentry(url.toString());

    // Flush first: navigating away would otherwise drop the event in flight.
    // Perform redirect (preserves session - no auth state clearing)
    Sentry.flush(2000)
      .catch(() => false)
      .finally(() => {
        window.location.href = url.toString();
      });
  }

  /**
   * Report the trip so an outage pages us instead of surfacing via support tickets.
   * 'network' failures with no HTTP status are the signature of a CDN/WAF block
   * (e.g. Cloudflare challenge pages failing CORS) rather than the API being down.
   */
  private reportToSentry(redirectUrl?: string): void {
    const statuses = this.state.failures.map(f => f.status);
    const networkCount = statuses.filter(s => s === 'network').length;
    const failureKind =
      networkCount === statuses.length ? 'network' : networkCount === 0 ? 'http' : 'mixed';

    Sentry.captureMessage('API health monitor tripped', {
      level: 'error',
      tags: {
        health_monitor: 'tripped',
        failure_kind: failureKind,
      },
      extra: {
        failures: this.state.failures,
        threshold: CONFIG.FAILURE_THRESHOLD,
        timeWindowMs: CONFIG.TIME_WINDOW_MS,
        pathname: window.location.pathname,
        online: typeof navigator !== 'undefined' ? navigator.onLine : undefined,
        redirectUrl,
      },
    });
  }

  /**
   * Get debug info for troubleshooting
   */
  getDebugInfo(): object {
    this.pruneStaleFailures();
    return {
      failures: this.state.failures,
      failureCount: this.state.failures.length,
      threshold: CONFIG.FAILURE_THRESHOLD,
      timeWindowMs: CONFIG.TIME_WINDOW_MS,
      sameEndpointDebounceMs: CONFIG.SAME_ENDPOINT_DEBOUNCE_MS,
      redirected: this.state.redirected,
      shouldRedirect: this.shouldRedirect(),
      currentPath: window.location.pathname,
      isMonitoring: this.shouldMonitor(),
      errorPageUrl: import.meta.env.ERROR_PAGE_URL || '(not configured)',
    };
  }

  /**
   * Reset state (for testing)
   */
  reset(): void {
    this.state = {
      failures: [],
      redirected: false,
    };
    this.pendingTrip = null;
  }
}

// Singleton instance
export const apiHealthMonitor = new ApiHealthMonitor();

// Expose debug method in development for troubleshooting via browser console
if (import.meta.env.DEV) {
  (window as unknown as Record<string, () => object>).__apiHealthDebug = () => apiHealthMonitor.getDebugInfo();
}
